<template>

  <div class="h-full flex flex-col">
    <div class="gap-2 self-stretch px-3 py-1 w-full text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
      MAIN MENU
    </div>
    <div class="flex-1 overflow-y-auto px-1 w-full text-base font-medium text-gray-600"
      style="max-height: calc(100vh - 84px);">

      <!-- Dynamic Main Menu -->
      <router-link v-for="(item, index) in filteredMenuItems" :key="index"
        @click="hideMenu" :to="{path: item.path}"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="[
          isActiveRoute(item.path) ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'
        ]">
        <i :class="`pi ${item.menuIcon} text-lg`"></i>
        <div class="flex-1">{{ dynamicMenuText(item) }}</div>
      </router-link>

      <!-- AI Workspace: per-school flag + ai_assistant.view grant -->
      <router-link :to="{ name: 'admin-ai-assistant' }"
        v-if="showAdminEntry"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/ai-assistant') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-sparkles text-lg"></i>
        <div class="flex-1">AI Workspace</div>
      </router-link>

      <div class="gap-2 self-stretch px-4 pt-6 pb-2 w-full text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
        ADMINISTRATION
      </div>

      <!-- Billing -->
      <router-link :to="{ name: routeNames.ADMIN_BILLING_DASHBOARD.name }"
        v-if="can(userStore.currentUser, AppPermissions.Billing.Actions.ViewBillItems, AppPermissions.Billing.Resource)"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/billing') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-wallet text-lg"></i>
        <div class="flex-1">Billing</div>
      </router-link>

      <!-- Certificates -->
      <router-link :to="{ name: routeNames.ADMIN_CERTIFICATES.name }"
        v-if="isTertiary && can(userStore.currentUser, AppPermissions.Certificates.Actions.View, AppPermissions.Certificates.Resource)"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/certificates') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-verified text-lg"></i>
        <div class="flex-1">Certificates</div>
      </router-link>

      <!-- Forms -->
      <router-link :to="{ name: 'admin-forms' }"
        v-if="can(userStore.currentUser, AppPermissions.Forms.Actions.View, AppPermissions.Forms.Resource)"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/forms') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-list text-lg"></i>
        <div class="flex-1">Forms</div>
      </router-link>

      <!-- Permissions -->
      <router-link :to="{ name: 'admin-permissions' }"
        v-if="can(userStore.currentUser, AppPermissions.Permissions.Actions.View, AppPermissions.Permissions.Resource)"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/permissions') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-lock text-lg"></i>
        <div class="flex-1">Permissions</div>
      </router-link>

      <!-- Support -->
      <router-link :to="{ name: 'admin-tickets' }"
        v-if="can(userStore.currentUser, AppPermissions.Tickets.Actions.View, AppPermissions.Tickets.Resource)"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/tickets') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-comments text-lg"></i>
        <div class="flex-1">Support</div>
      </router-link>

      <!-- Settings -->
      <router-link :to="{ name: 'admin-settings' }"
        v-if="can(userStore.currentUser, AppPermissions.SchoolProfile.Actions.View, AppPermissions.SchoolProfile.Resource)"
        @click="hideMenu"
        class="flex gap-3 items-center px-4 py-3 w-full rounded-lg transition-colors duration-200 mb-1"
        :class="isActiveRoute('/admin/settings') ? 'bg-primary text-white shadow-sm' : 'hover:bg-primary/10 hover:text-primary'">
        <i class="pi pi-cog text-lg"></i>
        <div class="flex-1">Settings</div>
      </router-link>

      <WhatsNewMenu mobile class="mt-4" @opened="hideMenu()" />

      <!-- Logout -->
      <div @click="showLogoutDialog = true"
        class="flex gap-3 cursor-pointer items-center px-4 py-3 w-full rounded-lg text-red-600 hover:bg-red-50 transition-colors duration-200">
        <i class="pi pi-sign-out text-lg"></i>
        <div class="flex-1">Log out</div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="showLogoutDialog" modal header="Confirm Logout" :style="{ width: '350px' }">
    <div class="mb-4">Are you sure you want to log out?</div>
    <div class="flex justify-end gap-2">
      <button class="px-4 py-2 rounded bg-gray-200" @click="showLogoutDialog = false">Cancel</button>
      <button class="px-4 py-2 rounded bg-red-600 text-white" @click="confirmLogout">Logout</button>
    </div>
  </Dialog>

</template>

<style scoped>
builder-component {
  max-width: none !important;
}
</style>

<script setup lang="ts">
import { routeNames, menubarItems, type MenuItem } from '@/constants/route-details.constant';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useAiAssistantAccess } from '@/composables/useAiAssistantAccess';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import SidebarMenuItem from '@/components/common/SidebarMenuItem.vue'; // We might need to adapt this or key off it, but for mobile we might just inline the style for now to match design
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import WhatsNewMenu from '@/components/common/WhatsNew/WhatsNewMenu.vue';

// Define props
defineProps({
  hideMenu: {
    type: Function,
    required: true,
  },
});

const router = useRouter();
const userStore = useUserStore();
const { school } = useSchoolQuery();
const { isTertiary } = useSchoolFeatures();
const { labels } = useSchoolLabels();
const { showAdminEntry } = useAiAssistantAccess();

// Reactive ref to track accordion open/close state
const accordionOpen = ref(false);
const showLogoutDialog = ref(false);

const route = useRoute();

const isActiveRoute = (path: string) => route.path === path || route.path.startsWith(path);
const isCollapsed = ref(false);

// Method to toggle sidebar state
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
};

const checkViewportSize = () => {
  isCollapsed.value = window.innerWidth < 1400;
};

onMounted(() => {
  checkViewportSize();
  window.addEventListener('resize', checkViewportSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewportSize);
});

// Provide the state and toggle function
provide('isCollapsed', isCollapsed);
provide('toggleSidebar', toggleSidebar);

function handleLogout() {
  // Use userStore logout if available, or manual clear
  userStore.logout();
  window.location.href = '/login';
}

const confirmLogout = async() => {
  showLogoutDialog.value = false;
  await handleLogout();
};

// Filter Menu Items (Same logic as Desktop)
const filteredMenuItems = computed(() => {
  if (!school.value || !userStore.currentUser) return [];

  const type = school.value.school_type?.toLowerCase();
  const isParent = (school.value as { is_parent?: boolean; parent_features_enabled?: boolean }).is_parent;
  const parentFeaturesEnabled = (school.value as { parent_features_enabled?: boolean }).parent_features_enabled;
  return menubarItems.filter(item => {
    // Permission Check
    if (item.permissions) {
      if (!item.permissions.some(p => can(userStore.currentUser, p.action, p.resource))) {
        return false;
      }
    } else if (item.permission) {
      if (!can(userStore.currentUser, item.permission.action, item.permission.resource)) {
        return false;
      }
    }

    if (item.showFor === 'parent') return !!isParent || !!parentFeaturesEnabled;
    if (item.showFor === 'all') return true;
    if (item.showFor === 'tertiary' && type === 'tertiary') return true;
    if (item.showFor === 'non-tertiary' && type !== 'tertiary') return true;
    return false;
  });
});

const dynamicMenuText = (item: MenuItem) => {
  if (item.routeName === 'admin-teacher') {
    return labels.value.TEACHER;
  }
  if (item.routeName === 'admin-student') {
    return labels.value.STUDENT + 's';
  }
  return item.menuText;
};

// Icon mapping to retain the svg images if possible, or fallback to PrimeIcons if preferred.
// The desktop sidebar uses PrimeIcons (pi-home, etc) from `menubarItems`.
// The mobile sidebar previously used SVGs from assets.
// To match desktop *logic* but mobile *design* (SVGs), we need a map or just switch to PrimeIcons for consistency?
// User said: "update the sidebar menu for mobile only to match this design" -> referencing the screenshot which likely uses specific icons.
// However, the screenshot looks like it uses PrimeIcons (or similar simple vector icons) -> e.g. "Dashboard" has a grid icon.
// The previous mobile code used `../assets/svg/dashboard.svg`.
// The desktop code uses `item.menuIcon` which are class names like `pi pi-home`.

// DECISION: The screenshot shows icons that look cleaner/simpler than the SVGs might be, or maybe they are the SVGs.
// But valid permission logic is key.
// I will try to use the `item.menuIcon` (PrimeIcons) for consistency and ease of iteration, unless user strictly wants SVGs.
// The screenshot provided by user has specific icons.
// Actually, looking at the screenshot, "Main Menu" has Dashboard, Grade System, Broadsheet, Students...
// These match `menubarItems`.
// I will use PrimeIcons defined in `menubarItems` to ensure it matches the metadata.
// This is safer than maintaining a separate mapping of SVGs.

</script>

<style>
/* Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

.aside {
  transition: width 0.3s ease;
}
</style>
